import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./liedBearbeiten.css";
import { kategorieLabel, nummerMitKuerzel } from "../buecher";
import { passtZurSuche } from "../suche";

const API = "/edit_add_api.php";

const blockTypes = [
  { value: "STROPHE", label: "Strophe" },
  { value: "CHOR", label: "Chor" }
];

const categories = [
  "",
  "auferstehung",
  "evangelisation",
  "gebet",
  "gnade",
  "golgatha",
  "himmlische_heimat",
  "hingabe_und_dienst",
  "kampf_und_sieg",
  "kinderlieder",
  "lob_und_anbetung",
  "trost_und_vertrauen",
  "weihnachten",
  "wiederkunft_christi",
  "wort_gottes"
];

function LiedBearbeiten() {
  const navigate = useNavigate();
  const redirectTimer = useRef(null);
  const audioInputRef = useRef(null);
  const [songs, setSongs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [form, setForm] = useState({ name: "", tonlage: "", kategorie: "", audio_url: "", bloecke: [] });
  const [audioFile, setAudioFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [pendingRemove, setPendingRemove] = useState(null);

  const filteredSongs = searchTerm.trim()
    ? songs.filter(s =>
        passtZurSuche(s, searchTerm, nummerMitKuerzel(s))
      )
    : [];

  useEffect(() => {
    fetch(API + "?action=list")
      .then(r => r.json())
      .then(setSongs);

    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, []);

  async function loadSong(id) {
    const res = await fetch(API + "?action=get&id=" + id);
    const data = await res.json();

    setSelected(id);
    setAudioFile(null);
    if (audioInputRef.current) {
      audioInputRef.current.value = "";
    }
    setForm({
      name: data.name,
      tonlage: data.tonlage || "",
      kategorie: data.kategorie || "",
      audio_url: data.audio_url || "",
      bloecke: data.bloecke || []
    });
  }

  function updateBlock(i, field, value) {
    const copy = [...form.bloecke];
    copy[i] = { ...copy[i], [field]: value };
    setForm({ ...form, bloecke: copy });
  }

  function addBlock() {
    setForm({
      ...form,
      bloecke: [...form.bloecke, { block_typ: "STROPHE", text: "" }]
    });
  }

  function confirmRemoveBlock() {
    if (pendingRemove === null) {
      return;
    }

    const copy = [...form.bloecke];
    copy.splice(pendingRemove, 1);
    setForm({ ...form, bloecke: copy });
    setPendingRemove(null);
  }

  function goHome() {
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
    }

    navigate("/");
  }

  async function save() {
    setIsSaving(true);
    setSaveError("");

    const body = new FormData();
    body.append("action", "update");
    body.append("id", selected);
    body.append("name", form.name);
    body.append("tonlage", form.tonlage);
    body.append("kategorie", form.kategorie);
    body.append("audio_url", form.audio_url || "");
    body.append("bloecke", JSON.stringify(form.bloecke));

    if (audioFile) {
      body.append("audio", audioFile);
    }

    try {
      const response = await fetch(API, {
        method: "POST",
        body
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Fehler beim Speichern");
      }

      setIsSaved(true);
      redirectTimer.current = setTimeout(() => navigate("/"), 7000);
    } catch (error) {
      setSaveError(error.message || "Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div id="lied_bearbeiten_body">
      {!isSaved && (
        <>
          <section className="edit_header">
            <div>
              <h2>Lied bearbeiten</h2>
              <p className="edit_eyebrow">Verwaltung</p>
            </div>
          </section>

          <section className="select_panel">
            <div className="search_wrapper">
              <input
                id="song_search"
                type="text"
                placeholder="Nach Lied suchen"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                autoComplete="off"
              />
              {showSuggestions && filteredSongs.length > 0 && (
                <div className="suggestions_list">
                  {filteredSongs.map(s => (
                    <div
                      key={s.id}
                      className="suggestion_item"
                      onClick={() => {
                        loadSong(s.id);
                        setSearchTerm("");
                        setShowSuggestions(false);
                      }}
                    >
                      <span className="suggestion_id">{nummerMitKuerzel(s)}</span>
                      <span className="suggestion_name">{s.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {showSuggestions && searchTerm.trim() && filteredSongs.length === 0 && (
                <div className="suggestions_list">
                  <div className="suggestion_empty">Keine Lieder gefunden</div>
                </div>
              )}
            </div>
          </section>

          {selected ? (
            <section className="song_editor">
              <div className="form_grid">
                <label className="full_width">
                  Titel
                  <input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                  />
                </label>

                <label>
                  Tonlage
                  <input
                    value={form.tonlage}
                    onChange={e => setForm({ ...form, tonlage: e.target.value })}
                  />
                </label>

                <label>
                  Kategorie
                  <select
                    value={form.kategorie}
                    onChange={e => setForm({ ...form, kategorie: e.target.value })}
                  >
                    {(categories.includes(form.kategorie) ? categories : [...categories, form.kategorie]).map(category => (
                      <option key={category || "none"} value={category}>
                        {kategorieLabel(category)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="audio_upload_field">
                  Audiodatei
                  <input
                    ref={audioInputRef}
                    id="audio_file_input"
                    type="file"
                    accept="audio/*,.mp3,.m4a,.wav,.ogg"
                    onChange={e => setAudioFile(e.target.files?.[0] || null)}
                  />
                  <label htmlFor="audio_file_input" className="audio_upload_button">
                    {audioFile ? audioFile.name : form.audio_url ? "Ändern" : "Hinzufügen"}
                  </label>
                </label>
              </div>

              <div className="blocks_head">
                <h3>Blöcke</h3>
                <span>{form.bloecke.length} Einträge</span>
              </div>

              <div className="blocks_list">
                {form.bloecke.map((b, i) => (
                  <article className="edit_block" key={i}>
                    <div className="block_topline">
                      <span className="block_number">{String(i + 1).padStart(2, "0")}</span>
                      <select
                        aria-label="Blocktyp"
                        value={b.block_typ || "STROPHE"}
                        onChange={e => updateBlock(i, "block_typ", e.target.value)}
                      >
                        {blockTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <textarea
                      aria-label={`Text Block ${i + 1}`}
                      value={b.text}
                      onChange={e => updateBlock(i, "text", e.target.value)}
                    />

                    <div className="block_actions">
                      <span>{(b.text || "").length} Zeichen</span>
                      <button
                        className="text_btn danger"
                        type="button"
                        onClick={() => setPendingRemove(i)}
                        disabled={form.bloecke.length <= 1}
                      >
                        Entfernen
                      </button>
                    </div>
              </article>
            ))}
          </div>

          <button className="add_block_btn" type="button" onClick={addBlock}>
            + Block
          </button>

          {saveError && (
            <p className="save_error" role="alert">
              {saveError}
            </p>
          )}

          <div className="editor_actions">
            <button
              className="primary_btn"
              type="button"
                  onClick={save}
                  disabled={!form.name.trim() || !form.kategorie || isSaving}
                >
                  {isSaving ? "Speichert..." : "Speichern"}
                </button>
              </div>
            </section>
          ) : (
            <section className="empty_state">
              <p>Wähle ein Lied aus, um Titel, Tonlage, Kategorie und Textblöcke zu bearbeiten.</p>
            </section>
          )}
        </>
      )}

      {pendingRemove !== null && (
        <div className="confirm_overlay" role="dialog" aria-modal="true" aria-labelledby="remove_block_title">
          <div className="confirm_dialog">
            <h3 id="remove_block_title">Block entfernen?</h3>
            <p>Möchtest du diesen Block wirklich entfernen?</p>
            <div className="confirm_actions">
              <button className="secondary_btn" type="button" onClick={() => setPendingRemove(null)}>
                Abbrechen
              </button>
              <button className="primary_btn danger_btn" type="button" onClick={confirmRemoveBlock}>
                Entfernen
              </button>
            </div>
          </div>
        </div>
      )}

      {isSaved && (
        <div className="success_overlay" role="status" aria-live="polite">
          <div className="success_message">
            <h3>Danke für deine Unterstützung</h3>
            <p>Die Änderung wird geprüft und dann freigegeben.</p>
            <button className="success_home_btn" type="button" onClick={goHome} aria-label="Zur Startseite">
              home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiedBearbeiten;
